import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert, Button, Grid, TextField, Chip, CircularProgress, Select, MenuItem, FormControl, InputLabel, Divider, Checkbox, FormControlLabel } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useCRM } from '../../../contexts/CRMContext';
import api from '../../../utils/api';

const CustomerStockSection = ({
  suggestions,
  linking,
  setLinking,
  selectedLocation,
  detectedSku,
  placementMode,
  setPlacementMode,
}) => {
  // Normalize SKUs to be delimiter-agnostic (ignore spaces/dashes/underscores, case-insensitive)
  const normalizeSku = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [markingArrived, setMarkingArrived] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searched, setSearched] = useState(false);

  const { getCustomers, customers, loading: loadingCustomers } = useCRM();

  // Determine if this is stock or customer from placementMode
  const isStock = placementMode === 'stock';
  const isStoreFront = placementMode === 'store_front';
  const customerName = suggestions?.matchedCustomer 
    ? `${suggestions.matchedCustomer.firstName} ${suggestions.matchedCustomer.lastName}`
    : suggestions?.customerName || '';

  // Initialize placement mode from suggestions when component mounts or suggestions change
  useEffect(() => {
    if (!placementMode) {
      if (suggestions?.isStock) {
        setPlacementMode && setPlacementMode('stock');
      } else {
        setPlacementMode && setPlacementMode('customer');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions]);

  // Auto-select matched customer on mount
  useEffect(() => {
    if (suggestions?.matchedCustomer && !linking.customerId) {
      // Automatically select the matched customer
      setLinking(prev => ({ ...prev, customerId: suggestions.matchedCustomer.id }));
    }
  }, [suggestions?.matchedCustomer]);

  // Fetch customer orders when a customer is selected
  useEffect(() => {
    if (linking.customerId && detectedSku) {
      fetchCustomerOrders(linking.customerId, detectedSku);
    }
  }, [linking.customerId, detectedSku]);

  // Helper to fetch orders for a customer filtered by SKU
  const fetchCustomerOrders = async (customerId, sku) => {
    if (!customerId || !selectedLocation?._id) return;
    try {
      setLoadingOrders(true);
      const skuParam = (sku || '').trim();
      const url = `/orders/customer/${customerId}?locationId=${selectedLocation._id}${skuParam ? `&sku=${encodeURIComponent(skuParam)}` : ''}`;
      const res = await api.get(url);
      if (res.data?.success) {
        setCustomerOrders(res.data.data.orders || []);
      }
    } catch (e) {
      console.error('Failed to fetch customer orders:', e);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Silent single-item arrival marker (no alerts, no internal refresh)
  // Optionally pass autoComplete=true to allow backend to mark order DELIVERED if all items are arrived
  const markItemArrivedSilent = async (orderId, itemId, { autoComplete = false } = {}) => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5006/api'}/orders/${orderId}/items/${itemId}/arrived`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ arrived: true, autoComplete })
        }
      );
    } catch (e) {
      console.error('Failed to mark item silently as arrived', e);
    }
  };

  // Mark ALL items in an order as arrived, then refresh once
  const markEntireOrderAsArrived = async (order) => {
    if (!order?.id || !Array.isArray(order.orderItems)) return;
    setMarkingArrived(true);
    try {
      const pending = order.orderItems.filter(it => !it.arrived);
      for (let i = 0; i < pending.length; i++) {
        const it = pending[i];
        const isLast = i === pending.length - 1;
        await markItemArrivedSilent(order.id, it.id, { autoComplete: isLast });
      }
      if (linking.customerId && detectedSku) {
        await fetchCustomerOrders(linking.customerId, detectedSku);
      }
    } catch (e) {
      console.error('Failed to mark entire order as arrived:', e);
      alert('Failed to mark entire order as arrived.');
    } finally {
      setMarkingArrived(false);
    }
  };

  // Mark order as complete (when all items have arrived)
  const markEntireOrderAsComplete = async (order) => {
    if (!order?.id) return;
    setMarkingArrived(true);
    try {
      // Use autoComplete flag to mark order as DELIVERED
      const lastItem = order.orderItems?.[order.orderItems.length - 1];
      if (lastItem) {
        await markItemArrivedSilent(order.id, lastItem.id, { autoComplete: true });
      }
      if (linking.customerId && detectedSku) {
        await fetchCustomerOrders(linking.customerId, detectedSku);
      }
      alert('Order marked as complete!');
    } catch (e) {
      console.error('Failed to mark order as complete:', e);
      alert('Failed to mark order as complete.');
    } finally {
      setMarkingArrived(false);
    }
  };

  // Auto-link first matching order (prefer items not yet arrived)
  useEffect(() => {
    if (customerOrders?.length > 0 && detectedSku && !linking.orderId) {
      const normalized = normalizeSku(detectedSku);
      // Prefer an order that has a matching item not arrived
      const preferred = customerOrders.find(o => o.orderItems?.some(it => normalizeSku(it.sku) === normalized && !it.arrived));
      const anyMatch = preferred || customerOrders.find(o => o.orderItems?.some(it => normalizeSku(it.sku) === normalized));
      if (anyMatch) {
        setLinking(prev => ({ ...prev, orderId: anyMatch.id }));
      }
    }
  }, [customerOrders, detectedSku, linking.orderId, setLinking]);

  const markItemAsArrived = async (orderId, itemId) => {
    setMarkingArrived(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5006/api'}/orders/${orderId}/items/${itemId}/arrived`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ arrived: true })
        }
      );
      const data = await response.json();
      if (data.success) {
        alert('Item marked as arrived!');
        // Refresh orders
        if (linking.customerId && detectedSku) {
          const fetchOrders = async () => {
            if (!linking.customerId || !selectedLocation?._id) return;
            try {
              setLoadingOrders(true);
              const skuParam = (detectedSku || '').trim();
              const url = `/orders/customer/${linking.customerId}?locationId=${selectedLocation._id}${skuParam ? `&sku=${encodeURIComponent(skuParam)}` : ''}`;
              const res = await api.get(url);
              if (res.data?.success) {
                setCustomerOrders(res.data.data.orders || []);
              }
            } catch (e) {
              console.error('Failed to fetch customer orders:', e);
            } finally {
              setLoadingOrders(false);
            }
          };
          fetchOrders();
        }
      } else {
        alert('Failed to mark item as arrived');
      }
    } catch (error) {
      console.error('Error marking item as arrived:', error);
      alert('Error marking item as arrived');
    } finally {
      setMarkingArrived(false);
    }
  };

  const handlePlacementChange = (e) => {
    const value = e.target.value;
    setPlacementMode && setPlacementMode(value);
    if (value !== 'customer') {
      // Clear linking when switching away from customer
      setLinking(prev => ({ ...prev, customerId: null, orderId: null }));
    }
  };

  const handleCustomerSearch = async () => {
    if (!selectedLocation?._id) return;
    setSearched(true);
    await getCustomers(selectedLocation._id, 1, 10, customerSearch.trim());
  };
  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" gutterBottom sx={{ m: 0 }}>
          {placementMode === 'customer' ? 'Customer: ' : placementMode === 'store_front' ? 'Store Front: ' : 'Stock: '}
          <Typography component="span" variant="h6" color="primary">
            {placementMode === 'customer' ? (customerName || 'Select Customer') : placementMode === 'store_front' ? 'Store Front' : 'Inventory Stock'}
          </Typography>
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="placement-mode-label">Placement</InputLabel>
          <Select
            labelId="placement-mode-label"
            label="Placement"
            value={placementMode || (suggestions?.isStock ? 'stock' : 'customer')}
            onChange={handlePlacementChange}
          >
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="stock">Stock</MenuItem>
            <MenuItem value="store_front">Store Front</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {suggestions ? (
        <>
          {placementMode === 'stock' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Detected label suggests this is <strong>STOCK</strong>.
            </Alert>
          )}

          {suggestions.matchedCustomer && placementMode === 'customer' ? (
            <Box sx={{ mb: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Customer Information</Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {suggestions.matchedCustomer.firstName} {suggestions.matchedCustomer.lastName}
                </Typography>
                {suggestions.matchedCustomer.email && (
                  <Typography variant="body2">
                    <strong>Email:</strong> {suggestions.matchedCustomer.email}
                  </Typography>
                )}
                {suggestions.matchedCustomer.phone && (
                  <Typography variant="body2">
                    <strong>Phone:</strong> {suggestions.matchedCustomer.phone}
                  </Typography>
                )}
                {suggestions.matchedCustomer.address && (
                  <Typography variant="body2">
                    <strong>Address:</strong> {
                      typeof suggestions.matchedCustomer.address === 'object' 
                        ? `${suggestions.matchedCustomer.address.street || ''} ${suggestions.matchedCustomer.address.city || ''} ${suggestions.matchedCustomer.address.state || ''} ${suggestions.matchedCustomer.address.zip || ''}`.trim()
                        : suggestions.matchedCustomer.address
                    }
                  </Typography>
                )}
              </Alert>
              {suggestions.matchReasons?.customer && placementMode === 'customer' && (
                <Typography variant="caption" color="text.secondary">
                  Match Reason: {suggestions.matchReasons.customer}
                </Typography>
              )}
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                <Button
                  variant={linking.customerId === suggestions.matchedCustomer.id ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setLinking((prev) => ({ ...prev, customerId: suggestions.matchedCustomer.id }))}
                >
                  Use This Customer
                </Button>
                <Button variant="text" size="small" onClick={() => setLinking((prev) => ({ ...prev, customerId: null }))}>
                  Clear
                </Button>
              </Box>
            </Box>
          ) : placementMode === 'customer' ? (
            <>
              {suggestions.customerName && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Customer Not Found</Typography>
                    <Typography variant="body2">
                      Detected name: <strong>{suggestions.customerName}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      No matching customer found in the database. Use the search below to select the correct customer.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </>
          ) : null}

          {suggestions.matchedOrder && placementMode === 'customer' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Matched Order</Typography>
              <Typography variant="body2">
                #{suggestions.matchedOrder.orderNumber} • {suggestions.matchedOrder.status}
              </Typography>
              {suggestions.matchReasons?.order && (
                <Typography variant="caption" color="text.secondary">
                  Reason: {suggestions.matchReasons.order}
                </Typography>
              )}
              {/* Read-only checklist of items in the matched order */}
              {Array.isArray(suggestions.matchedOrder.orderItems) && suggestions.matchedOrder.orderItems.length > 0 ? (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  {suggestions.matchedOrder.orderItems.map((item) => {
                    const isDetected = normalizeSku(item.sku) === normalizeSku(detectedSku);
                    const checked = item.arrived || isDetected;
                    return (
                      <FormControlLabel
                        key={item.id}
                        control={<Checkbox size="small" checked={checked} disabled />}
                        label={`${item.name || item.sku} — Qty: ${item.quantity} ${item.arrived ? '(Arrived)' : isDetected ? '(Detected now)' : ''}`}
                      />
                    );
                  })}
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      disabled={!Array.isArray(suggestions.matchedOrder.orderItems) || markingArrived}
                      onClick={() => markEntireOrderAsArrived(suggestions.matchedOrder)}
                    >
                      Mark Order As All Items Arrived
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Order items not loaded yet. Select the customer below to load order details.
                </Typography>
              )}
            </Box>
          )}

          {Array.isArray(suggestions.skuCandidates) && suggestions.skuCandidates.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                SKU Candidates
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {suggestions.skuCandidates.slice(0, 12).map((sku, idx) => (
                  <Chip key={idx} size="small" label={sku} />
                ))}
              </Box>
            </Box>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No auto customer/stock suggestions in this result. Use the customer search below.
        </Typography>
      )}

      {/* Customer search area when placement is customer */}
      {placementMode === 'customer' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Find Customer</Typography>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={8} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email or phone"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCustomerSearch(); } }}
              />
            </Grid>
            <Grid item>
              <Button size="small" variant="contained" onClick={handleCustomerSearch} disabled={loadingCustomers}>
                {loadingCustomers ? 'Searching...' : 'Search'}
              </Button>
            </Grid>
          </Grid>
          {searched && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">{customers?.length || 0} result(s)</Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1}>
                {customers?.map((c) => (
                  <Grid item xs={12} md={6} key={c.id}>
                    <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{c.firstName} {c.lastName}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.email || c.phone || '—'}</Typography>
                      </Box>
                      <Button size="small" variant={linking.customerId === c.id ? 'contained' : 'outlined'} onClick={() => setLinking(prev => ({ ...prev, customerId: c.id, orderId: null }))}>
                        {linking.customerId === c.id ? 'Selected' : 'Select'}
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {/* Customer Orders with matching SKU */}
      {linking.customerId && placementMode === 'customer' && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            <LocalShippingIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
            Orders with SKU: {detectedSku || 'N/A'}
          </Typography>
          {loadingOrders ? (
            <CircularProgress size={24} />
          ) : customerOrders.length > 0 ? (
            <Box sx={{ mt: 1 }}>
              {customerOrders.map((order) => {
                // Check if all items in the order have arrived
                const allItemsArrived = order.orderItems?.every(item => item.arrived) || false;
                
                return (
                  <Box key={order.id} sx={{ mb: 2, p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight="bold">
                      Order #{order.orderNumber} - {order.status}
                    </Typography>
                    
                    {/* Show Mark Order as Complete button if all items arrived */}
                    {allItemsArrived && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          onClick={() => markEntireOrderAsComplete(order)}
                          disabled={markingArrived}
                        >
                          Mark Order as Complete
                        </Button>
                      </Box>
                    )}
                    
                    {order.orderItems?.filter(item => normalizeSku(item.sku) === normalizeSku(detectedSku)).map((item) => (
                      <Box key={item.id} sx={{ mt: 1, pl: 2 }}>
                        <Typography variant="body2">
                          {item.name} - Qty: {item.quantity}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.arrived ? (
                            <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Arrived" />
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LocalShippingIcon />}
                              disabled={markingArrived}
                              onClick={() => markItemAsArrived(order.id, item.id)}
                            >
                              Mark Received Item for Order
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No orders found with this SKU
            </Typography>
          )}
        </Box>
      )}

      {/* New customer creation removed per requirements */}
    </Box>
  );
};

export default CustomerStockSection;
